import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { creditTiers, interestRateToCreditTiers } from '../../common/constants';
import { LoanTerms } from '../../common/types';
import { calculateMortgage } from '../../mortrage-lib/monthlyPayment';
import {
  MonthlyPaymentContextType,
  MonthlyPaymentDataActions,
  MonthlyPaymentDataActionsTypes,
  MonthlyPaymentState
} from '../types';
import {
  setDefaults,
  setDownPayment,
  setDownPaymentPercent,
  setHoaFeesMonthly,
  setHomeownersInsuranceMonthly,
  setHomePrice,
  setInterestRate,
  setLoanTerm,
  setPropertyTaxAnnual,
  setShowFull
} from './monthlyPaymentSetters';

const initialState: MonthlyPaymentState = {
  homePrice: 250000,
  downPayment: 50000,
  downPaymentPercent: 20,
  loanTerm: LoanTerms.THIRTY,
  interestRate: interestRateToCreditTiers[creditTiers['740+']],
  propertyTaxAnnual: 3000,
  homeownersInsuranceMonthly: 100,
  hoaFeesMonthly: 0,
  showFull: false
};

export const LoanTermToNumber = {
  [LoanTerms.THIRTY]: 30,
  [LoanTerms.TWENTY]: 20,
  [LoanTerms.FIFTEEN]: 15
};

// eslint-disable-next-line abcsize/abcsize
function calculatorReducer(
  state: MonthlyPaymentState,
  action: MonthlyPaymentDataActions
): MonthlyPaymentState {
  switch (action.type) {
    case MonthlyPaymentDataActionsTypes.SET_HOME_PRICE:
      return setHomePrice(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_DOWN_PAYMENT:
      return setDownPayment(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_DOWN_PAYMENT_PERCENT:
      return setDownPaymentPercent(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_LOAN_TERM:
      return setLoanTerm(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_INTEREST_RATE:
      return setInterestRate(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_PROPERTY_TAX_ANNUAL:
      return setPropertyTaxAnnual(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_HOMEOWNTERS_INSURANCE_MONTHLY:
      return setHomeownersInsuranceMonthly(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_HOA_FEES_MONTHLY:
      return setHoaFeesMonthly(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_SHOW_FULL:
      return setShowFull(state, action.payload);
    case MonthlyPaymentDataActionsTypes.SET_DEFAULTS:
      return setDefaults(state, initialState);
    default:
      throw new Error(`Unhandled action type`);
  }
}

const MonthlyPaymentContext = createContext<MonthlyPaymentContextType>({
  state: initialState,
  dispatch: () => null,
  getMonthlyPayment: () => 0,
  getPropertyTaxesMonthly: () => 0,
  getMonthlyPrincipalAndInterest: () => 0,
  getMortgageSize: () => 0,
  getMortgageInterest: () => 0,
  getTotalMortgagePaind: () => 0
});

export function MonthlyPaymentProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const {
    monthlyPayment,
    mortgageSize,
    totalInterest,
    totalPaid,
    monthlyPropertyTaxes,
    monthlyPrincipalAndInterest
  } = calculateMortgage({
    homePrice: state.homePrice,
    downPayment: state.downPayment,
    loanTermYears: LoanTermToNumber[state.loanTerm],
    apr: state.interestRate,
    propertyTaxAnnual: state.propertyTaxAnnual,
    homeownersInsuranceMonthly: state.homeownersInsuranceMonthly,
    hoaFeesMonthly: state.hoaFeesMonthly
  });

  const getMonthlyPayment = () => monthlyPayment;
  const getPropertyTaxesMonthly = () => monthlyPropertyTaxes;
  const getMonthlyPrincipalAndInterest = () => monthlyPrincipalAndInterest;
  const getMortgageSize = () => mortgageSize;
  const getMortgageInterest = () => totalInterest;
  const getTotalMortgagePaind = () => totalPaid;

  const value = {
    state,
    dispatch,
    getMonthlyPayment,
    getPropertyTaxesMonthly,
    getMonthlyPrincipalAndInterest,
    getMortgageSize,
    getMortgageInterest,
    getTotalMortgagePaind
  };

  return <MonthlyPaymentContext.Provider value={value}>{children}</MonthlyPaymentContext.Provider>;
}

export function useMonthlyPayment() {
  const context = useContext(MonthlyPaymentContext);
  if (context === undefined) {
    throw new Error('useMonthlyPayment must be used within a MonthlyPaymentProvider');
  }
  return context;
}
